import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Alert, Button, Card, Select, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { formatDay, todayIso } from '../projects/data';
import type { Period, Summary, SummaryTask, SummaryTicket } from './data';

/** Mueve una fecha AAAA-MM-DD un período (semana o mes) hacia atrás o hacia adelante. */
function shift(date: string, period: Period, step: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  if (period === 'semanal') d.setUTCDate(d.getUTCDate() + 7 * step);
  else d.setUTCMonth(d.getUTCMonth() + step, 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Resumen semanal o mensual por reglas (guía 7.3): Qué se hizo / Qué sigue en curso / Qué viene / Atención y el párrafo
 * de cierre, para el weekly. Personal o de un proyecto (?proyecto=…).
 */
export function SummaryPage() {
  const [params, setParams] = useSearchParams();
  const period: Period = params.get('periodo') === 'mensual' ? 'mensual' : 'semanal';
  const date = params.get('fecha') ?? todayIso();
  const projectNumber = params.get('proyecto');
  const [copied, setCopied] = useState(false);

  const query = new URLSearchParams({ periodo: period, fecha: date });
  if (projectNumber) query.set('proyecto', projectNumber);
  const summary = useQuery({ queryKey: ['tareas', 'resumen', period, date, projectNumber], queryFn: () => api<Summary>(`/api/tareas/resumen?${query}`), placeholderData: (prev) => prev });

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
    setCopied(false);
  };

  const s = summary.data;
  const copy = async () => {
    if (!s) return;
    try {
      await navigator.clipboard.writeText(s.closing);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-4">
      <div>
        {s?.scope.type === 'proyecto' ? (
          <Link to={`/proyectos/${s.scope.number}`} className="text-sm text-marca hover:underline">
            ← {s.scope.code} · {s.scope.name}
          </Link>
        ) : (
          <Link to="/tareas" className="text-sm text-marca hover:underline">
            ← {t('ta.titulo')}
          </Link>
        )}
        <h1 className="mt-1 text-2xl font-semibold text-tinta">{s?.scope.type === 'proyecto' ? t('re.titulo_proyecto', { proyecto: s.scope.name }) : t('re.titulo')}</h1>
        <p className="text-sm text-slate-600">{t('re.descripcion')}</p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-40">
          <Select id="re-periodo" label={t('re.periodo')} value={period} onChange={(e) => update({ periodo: e.target.value === 'semanal' ? null : e.target.value })}>
            <option value="semanal">{t('re.semanal')}</option>
            <option value="mensual">{t('re.mensual')}</option>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => update({ fecha: shift(date, period, -1) })}>
            ← {t(period === 'semanal' ? 're.anterior_semana' : 're.anterior_mes')}
          </Button>
          <Button variant="secondary" onClick={() => update({ fecha: null })}>
            {t(period === 'semanal' ? 're.esta_semana' : 're.este_mes')}
          </Button>
          <Button variant="secondary" onClick={() => update({ fecha: shift(date, period, 1) })}>
            {t(period === 'semanal' ? 're.siguiente_semana' : 're.siguiente_mes')} →
          </Button>
        </div>
      </div>

      {!s ? (
        summary.error ? <Alert>{errorMessage(summary.error)}</Alert> : <Spinner label={t('comun.cargando')} />
      ) : (
        <>
          <Card title={s.label}>
            <p className="text-sm text-slate-800">{s.closing}</p>
            <div className="mt-3 flex items-center gap-3">
              <Button variant="secondary" onClick={() => void copy()}>
                {t('re.copiar')}
              </Button>
              {copied && (
                <span role="status" className="text-xs text-green-700">
                  {t('re.copiado')}
                </span>
              )}
            </div>
          </Card>
          <div className="grid gap-4 md:grid-cols-2">
            <Section title={t('re.hecho')} tasks={s.done.tasks} tickets={s.done.tickets} empty={t('re.hecho_vacio')} />
            <Section title={t('re.en_curso')} tasks={s.inProgress.tasks} tickets={s.inProgress.tickets} empty={t('re.en_curso_vacio')} />
            <Section title={t('re.viene', { desde: formatDay(s.nextStart), hasta: formatDay(s.nextEnd) })} tasks={s.upcoming.tasks} tickets={s.upcoming.tickets} empty={t('re.viene_vacio')} />
            <Section title={t('re.atencion')} tasks={s.attention.tasks} tickets={s.attention.tickets} empty={t('re.atencion_vacio')} warn />
          </div>
        </>
      )}
    </div>
  );
}

function Section({ title, tasks, tickets, empty, warn = false }: { title: string; tasks: SummaryTask[]; tickets: SummaryTicket[]; empty: string; warn?: boolean }) {
  const total = tasks.length + tickets.length;
  return (
    <Card title={`${title} (${total})`} className={warn && total > 0 ? 'border-red-200' : ''}>
      {total === 0 ? (
        <p className="text-sm text-slate-500">{empty}</p>
      ) : (
        <ul className="space-y-1.5 text-sm" aria-label={title}>
          {tasks.map((x) => (
            <li key={x.id}>
              <Link to={`/tareas/${x.id}`} className="text-slate-900 hover:underline">
                {x.title}
              </Link>
              <span className="block text-xs text-slate-500">
                {[x.project ? `${x.project.code} · ${x.project.name ?? ''}` : t('ta.personal'), x.completedOn ? t('re.terminada_el', { fecha: formatDay(x.completedOn) }) : x.dueDate ? t('re.vence_el', { fecha: formatDay(x.dueDate) }) : null]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </li>
          ))}
          {tickets.map((x) => (
            <li key={x.number}>
              <Link to={`/tickets/${x.number}`} className="text-slate-900 hover:underline">
                <span className="mr-1 font-mono text-xs text-slate-500">{x.code}</span>
                {x.title}
              </Link>
              <span className="block text-xs text-slate-500">{x.resolvedOn ? t('re.resuelto_el', { fecha: formatDay(x.resolvedOn) }) : x.status}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
